import type { CodeSystem } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import { capturableQuestions } from '@seen/core/terminology/capturable'
import {
  libraryCodingDetails,
  TERMINOLOGY_LIBRARY,
} from '@seen/core/terminology/library'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@seen/ui'
import { type JSX, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { Input } from '#src/components/core/input/Input.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { observationDetailsSummary } from '#src/components/rich-text-editor/mention/observationDetailsSummary.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { NotePlayground } from '#src/pages/observations/NotePlayground.tsx'

// Our own codes that can be recorded as observations in a note, and a scratch
// note to try them in. The codes are defined in packages/core/src/terminology
// and change by pull request.
export interface ObservationsPageProps {
  questionCodes?: CodeSystem
}

export function ObservationsPage({
  questionCodes = TERMINOLOGY_LIBRARY.questionCodes,
}: ObservationsPageProps): JSX.Element {
  const enabled = useFeatureFlag(featureFlags.structuredCapture)
  if (!enabled) {
    return (
      <PageLayout title="Observations">
        <p className="max-w-xl text-sm text-muted-foreground">
          Observations are behind the structured-capture flag, which is off for
          you.
        </p>
      </PageLayout>
    )
  }
  return (
    <PageLayout title="Observations">
      <div className="flex flex-col gap-4 pb-8">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Codes clinicians can record in a note from the @ picker. Each answer
          is saved as an observation that can be tracked. The codes are defined
          in code and change by pull request.
        </p>
        <Tabs defaultValue="codes">
          <TabsList>
            <TabsTrigger value="codes">Codes</TabsTrigger>
            <TabsTrigger value="playground">Try in a note</TabsTrigger>
          </TabsList>
          <TabsContent value="codes">
            <CodeTable questionCodes={questionCodes} />
          </TabsContent>
          <TabsContent value="playground">
            <NotePlayground />
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  )
}

function CodeTable({
  questionCodes,
}: {
  questionCodes: CodeSystem
}): JSX.Element {
  // A link from a field's configuration arrives with its code.
  const [params] = useSearchParams()
  const [search, setSearch] = useState(() => params.get('code') ?? '')
  const rows = useMemo(
    () =>
      capturableQuestions(questionCodes, search).map((question) => ({
        ...question,
        details: libraryCodingDetails(question),
      })),
    [questionCodes, search]
  )
  return (
    <div className="flex flex-col gap-3">
      <Input
        aria-label="Search codes"
        placeholder="Search by name, code or wording…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="max-w-md"
      />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Label</TableHead>
            <TableHead>Code</TableHead>
            <TableHead>Answered with</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.code}>
              <TableCell>
                <div>{row.display}</div>
                {row.definition && row.definition !== row.display ? (
                  <div className="text-xs text-muted-foreground">
                    {row.definition}
                  </div>
                ) : null}
              </TableCell>
              <TableCell className="font-mono text-xs">{row.code}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {row.details ? observationDetailsSummary(row.details) : ''}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground">
                No codes match.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  )
}
